import _ from "lodash";
import jwt_decode from "jwt-decode";
import {
  DEFAULT_STATUS_MISSION,
  HTTP_STATUS_CODE,
  SCOPES,
} from "constants/constants";
import {
  getSSOJwt,
  getUserFiltersQuery,
  login,
  mascaradeAsQuery,
  postUserFiltersQuery,
} from "./queries";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import moment from "moment";
import { formatFilterIntoString } from "utils/missionHelper";
import { lastDayOfTheMonthFormatted } from "utils/sharedDashboardHelper";
import { getDefaultRoutes } from "utils/helper";

const UserContext = createContext({});

export const useUserContext = () => useContext(UserContext);

export const UserProvider = ({ ...props }) => {
  const { location } = document;

  const navigate = useCallback(
    (path) => {
      location.href = path;
    },
    [location]
  );

  const [lang, setLang] = useState("fr");
  const [response, setResponse] = useState(null);
  const [ssoReply, setSsoReply] = useState({});
  const [showLoginFallbackHint, setShowLoginFallbackHint] = useState(false);
  const [mascaradeUser, setMascaradeUser] = useState({});
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [isTouched, setIsTouched] = useState(false);
  const [userScope, setUserScope] = useState(null);
  const [userMissionsFilters, setUserMissionsFilters] = useState(null);
  const [userScopeList, setUserScopeList] = useState([]);
  const [userRoleList, setUserRoleList] = useState([]);
  const defaultFilters = {
    missions: {
      status: formatFilterIntoString(DEFAULT_STATUS_MISSION),
      start_from_date: "",
      start_to_date: "",
      end_from_date_or_null: "",
      end_to_date: "",
    },
    dashboard: {
      date_filter: moment().format("YYYY-MM-DD"),
    },
    shared_dashboard: {
      ic: {
        intercontracts_subsidiaries: [],
        intercontracts_managers: [],
        intercontracts_domain_categories: [],
        intercontracts_domains: [],
      },
      fdm: {
        missions_exits_activation_subsidiaries: [],
        missions_exits_activation_managers: [],
        missions_exits_activation_domain_categories: [],
        missions_exits_activation_domains: [],
      },
      ed: {
        direct_hiring_subsidiaries: [],
        manager_mission_ed: [],
        dav_function_category_ed: [],
        dav_function_ed: [],
        date: "",
        mobility: "",
      },
      ok: {
        ok_subsidiaries: [],
        ok_managers: [],
      },
      cl: {
        long_leave_subsidiaries: [],
        long_leave_managers: [],
      },
      ko: {
        companies_exits_subsidiaries: [],
        manager_companies_exits: [],
        companies_exits_date: lastDayOfTheMonthFormatted,
        exits_companies_by: "",
      },
    },
    tools_manager: {
      experience: null,
      branch_id: [],
      domain_category_id: [],
      domain_id: [],
    },
  };
  const [userFilterStorage, setUserFilterStorage] = useState(defaultFilters);
  const [apiErrorResponse, setApiErrorResponse] = useState(null);

  const postUserFilters = useCallback(async (filters, key) => {
    await postUserFiltersQuery(filters);
    await getUserFilters(key);
  }, []);

  const getUserFilters = useCallback(async (key) => {
    const applyDefaultFilters = (filterKey) => {
      const defaults = defaultFilters[filterKey];
      if (!defaults) {
        console.warn(`No default filters defined for key: ${filterKey}`);
        return;
      }
      setUserFilterStorage(defaults);

      // Persist defaults to DB in background (fire-and-forget)
      postUserFiltersQuery({
        key: filterKey,
        value: JSON.stringify(defaults),
      }).catch((e) => {
        console.warn(`Failed to persist default filters for ${filterKey}:`, e);
      });
    };

    try {
      const newFilters = await getUserFiltersQuery(key);

      if (newFilters === null) {
        applyDefaultFilters(key);
        return;
      }

      const parsedFilters = newFilters?.value
        ? JSON.parse(newFilters.value)
        : newFilters.value;
      setUserFilterStorage(parsedFilters);
    } catch (error) {
      if (error.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
        // First connection: apply defaults immediately, persist in background
        applyDefaultFilters(key);
        return;
      }
      // For other errors, still apply defaults to not block the user
      console.error(`Error fetching filters for ${key}:`, error);
      applyDefaultFilters(key);
    }
  }, []);

  const connectedUser = useMemo(
    () => ({
      id:
        Number(localStorage.getItem("connected_as")) ||
        Number(localStorage.getItem("id")),
    }),
    [token, mascaradeUser]
  );

  const checkUserToken = () => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (e) {
      localStorage.removeItem("user");
      return null;
    }
  };
  const [user, setUser] = useState(checkUserToken());

  useEffect(() => {
    localStorage.setItem("user_scope", userScope);
  }, [userScope]);

  useEffect(() => {
    if (!token) {
      const token = localStorage.getItem("token");
      token && setToken(token);
    }
    const loginPathSegments = location.pathname.split("/").filter(Boolean);
    const isOnLoginPath =
      (loginPathSegments.length === 1 && loginPathSegments[0] === "login") ||
      (loginPathSegments.length === 2 &&
        loginPathSegments[1] === "login" &&
        Object.values(SCOPES).includes(loginPathSegments[0]));
    if (token && isOnLoginPath) {
      if (!userRoleList?.length) return;
      const defaultRoute = getDefaultRoutes(userRoleList) || "/unauthorized";
      return navigate(`/${userScope || SCOPES.PERSO}${defaultRoute}`);
    }
    if (response !== null) {
      setUser(response);
      if (token !== null) {
        const defaultRoute = getDefaultRoutes(userRoleList) || "/unauthorized";
        if (defaultRoute) {
          navigate(`/${userScope || SCOPES.PERSO}${defaultRoute}`);
        }
        return;
      }
    }
    if (token) {
      localStorage.setItem("user", JSON.stringify(user));
    }

    if (ssoReply?.token) {
      localStorage.setItem("token", ssoReply.token);
      localStorage.setItem("id", jwt_decode(token)?.sub);
      localStorage.setItem("user", JSON.stringify(user));
    }

    if (
      !token &&
      location.pathname !== "/login" &&
      location.pathname !== "/authorize" &&
      location.pathname !== "/logout" &&
      !_.startsWith(location.pathname, "/balancesheet/mission/slug") &&
      !_.startsWith(location.pathname, "/davidson-evaluation/mission") &&
      !_.startsWith(location.pathname, "/balancesheet/annual/slug") &&
      !_.startsWith(
        location.pathname,
        "/davidson-evaluation/davidson/consultant"
      )
    ) {
      navigate("/login");
    }
  }, [navigate, user, response, token, location, ssoReply, userRoleList]);

  useEffect(() => {
    if (token) {
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
      const scopes = JSON.parse(localStorage.getItem("scopes") || "[]");
      setUserRoleList(roles);
      setUserScopeList(scopes);
    }
  }, [token]);

  const saveUserDataToLocalStorage = (loginData) => {
    localStorage.setItem("token", loginData.token);
    localStorage.setItem("id", jwt_decode(loginData.token)?.sub);
    localStorage.setItem("roles", JSON.stringify(loginData.role || []));
    localStorage.setItem("scopes", JSON.stringify(loginData.scope || []));
  };

  const userLogin = useCallback(async (email, password) => {
    try {
      const loginData = await login(email, password);
      if (loginData.token) {
        localStorage.clear();
        saveUserDataToLocalStorage(loginData);
        setResponse(loginData);
        setUserRoleList(loginData?.role);
        setUserScopeList(loginData?.scope);
        setToken(loginData.token);
        setUserScope(SCOPES.PERSO);
        setShowLoginFallbackHint(false);
      } else {
        setResponse({ error: "Vos identifiants ne sont pas valides" });
      }
    } catch (e) {
      console.error("userLogin", e);
      setResponse({ error: "Vos identifiants ne sont pas valides" });
    }
  }, []);

  const userSetLang = useCallback(
    (val) => {
      if (val) setLang(val);
    },
    [setLang]
  );

  const userSetScope = useCallback(
    (scope) => {
      setUserScope(scope || SCOPES.PERSO);
    },
    [setUserScope]
  );

  const logout = useCallback(() => {
    localStorage.clear();
    setResponse(null);
    setUser(null);
    setToken(null);
    setSsoReply({});
    setUserRoleList([]);
    setUserScopeList([]);
    setUserScope(null);
    navigate("/login");
  }, [navigate, setUser, setToken]);

  const sendTokenToBack = useCallback(
    async (data) => {
      try {
        const reply = await getSSOJwt(data);
        if (!reply || !reply.token) {
          setShowLoginFallbackHint(true);
          return;
        }
        setUser({ full_name: reply.full_name, email: reply.email });
        setToken(reply.token);
        setSsoReply(reply);
        localStorage.setItem("token", reply.token);
        localStorage.setItem("id", jwt_decode(reply.token)?.sub);
        localStorage.setItem("roles", JSON.stringify(reply.role || []));
        localStorage.setItem("scopes", JSON.stringify(reply.scope || []));
        localStorage.setItem(
          "user",
          JSON.stringify({
            full_name: reply.full_name,
            email: reply.email,
          })
        );
        setUserRoleList(reply.role || []);
        setUserScopeList(reply.scope || []);
      } catch (e) {
        console.error("sendTokenToBack", e);
        setShowLoginFallbackHint(true);
        throw e;
      }
    },
    [setSsoReply]
  );

  const clearUserFiltersFromLocalStorage = () => {
    // Tools Manager filters
    localStorage.removeItem("tools_manager_abaque_filters");
    localStorage.removeItem("tools_missions_mission-title-filter");
    localStorage.removeItem("tools_missions_mission-job-filter");
    localStorage.removeItem("tools_missions_mission-job-category-filter");
    localStorage.removeItem("tools_missions_mission-sector-filter");
    localStorage.removeItem("tools_missions_mission-status-filter");
    localStorage.removeItem("tools_missions_mission-star-only-filter");
    localStorage.removeItem("tools_missions_mission-impact-only-filter");
    localStorage.removeItem("tools-prospections_client-filter");
    localStorage.removeItem("tools-clients_status-filter");
    // Shared Dashboard filters
    localStorage.removeItem("shared_dashboard_ic_filters");
    localStorage.removeItem("shared_dashboard_fdm_filters");
    localStorage.removeItem("shared_dashboard_ed_filters");
    localStorage.removeItem("shared_dashboard_ok_filters");
    localStorage.removeItem("shared_dashboard_cl_filters");
    localStorage.removeItem("shared_dashboard_ss_filters");
    localStorage.removeItem("shared_dashboard_user_id");
    ["ic", "fdm", "ed", "ok", "cl", "ko"].forEach((tab) => {
      localStorage.removeItem(`shared_dashboard_${tab}_subsidiary`);
    });
  };

  const mascaradeAs = useCallback(
    async (email) => {
      const result = await mascaradeAsQuery(email);
      if (!_.isEmpty(result)) {
        if (!localStorage.getItem("original_token")) {
          localStorage.setItem("original_token", localStorage.getItem("token"));
          localStorage.setItem("original_roles", localStorage.getItem("roles"));
          localStorage.setItem(
            "original_scopes",
            localStorage.getItem("scopes")
          );
        }
        clearUserFiltersFromLocalStorage();
        setUserFilterStorage(null);
        localStorage.setItem("mascarade_token", result.token);
        localStorage.setItem("connected_as", result.eosid);
        localStorage.setItem("roles", JSON.stringify(result?.role || []));
        localStorage.setItem("scopes", JSON.stringify(result?.scope || []));
        setToken(result.token);
        setUserRoleList(result.role);
        setUserScopeList(result.scope);
        setUserScope(SCOPES.PERSO);
      }
      setMascaradeUser(result);
      const defaultRoute = getDefaultRoutes(result.role) || "/unauthorized";

      navigate(`/perso${defaultRoute}`);
    },
    [setMascaradeUser]
  );

  const backInitialAccount = useCallback(async () => {
    const originalToken = localStorage.getItem("original_token");
    const originalRoles = localStorage.getItem("original_roles");
    const originalScopes = localStorage.getItem("original_scopes");

    if (originalToken) {
      localStorage.setItem("token", originalToken);
      setToken(originalToken);
    }
    if (originalRoles) {
      localStorage.setItem("roles", originalRoles);
      setUserRoleList(JSON.parse(originalRoles));
    }
    if (originalScopes) {
      localStorage.setItem("scopes", originalScopes);
      setUserScopeList(JSON.parse(originalScopes));
    }

    clearUserFiltersFromLocalStorage();
    setUserFilterStorage(null);
    localStorage.removeItem("mascarade_token");
    localStorage.removeItem("connected_as");
    localStorage.removeItem("original_token");
    localStorage.removeItem("original_roles");
    localStorage.removeItem("original_scopes");

    setMascaradeUser({});
    navigate("/perso/dashboard/shared/ic");
  }, [setMascaradeUser]);

  const values = useMemo(
    () => ({
      userScopeList,
      userRoleList,
      userMissionsFilters,
      setUserMissionsFilters,
      postUserFilters,
      getUserFilters,
      backInitialAccount,
      connectedUser,
      lang,
      logout,
      mascaradeAs,
      mascaradeUser,
      response,
      sendTokenToBack,
      ssoReply,
      showLoginFallbackHint,
      setShowLoginFallbackHint,
      token,
      user,
      userLogin,
      userScope,
      userSetLang,
      userSetScope,
      setUserScope,
      isTouched,
      setIsTouched,
      apiErrorResponse,
      setApiErrorResponse,
    }),
    [
      userScopeList,
      userRoleList,
      userMissionsFilters,
      setUserMissionsFilters,
      postUserFilters,
      getUserFilters,
      backInitialAccount,
      connectedUser,
      lang,
      logout,
      mascaradeAs,
      mascaradeUser,
      response,
      sendTokenToBack,
      ssoReply,
      showLoginFallbackHint,
      setShowLoginFallbackHint,
      token,
      user,
      userLogin,
      userScope,
      userSetLang,
      userSetScope,
      setUserScope,
      isTouched,
      setIsTouched,
      apiErrorResponse,
      setApiErrorResponse,
    ]
  );

  return (
    <UserContext.Provider value={{ ...values, userFilterStorage }} {...props} />
  );
};
